import { Chip, Stack, Tooltip, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "i18n-fe";
import { TEST_DISAGREEMENT_SIGNALS } from "score-common";

// #2252 -- one static, literal i18n key per signal (score-common's
// TEST_DISAGREEMENT_SIGNALS, computed by #2251's computeTestDisagreement).
// Same table-indexed pattern as FvdBindingChip's BINDING_SIGNAL_LABEL_KEYS
// (apps/scoreadmin-fe/src/components/FvdBindingChip.jsx) -- i18n-fe needs a
// literal key at each t() call site, so this indexes a finite, hand-written
// table rather than building a key string from the signal at runtime. A
// signal not in this table (a future one added to score-common before this
// table catches up) falls back to the raw signal code below, same as
// BindingSignalChip's fallback, rather than disappearing or throwing.
const SIGNAL_DETAIL_KEYS = {
  [TEST_DISAGREEMENT_SIGNALS.CLASS_RIDE_MISMATCH]: "ring.testDisagreement.classRideMismatch",
};

function testLabel(id, testsById) {
  if (!id) return id;
  return testsById?.[id]?.name || testsById?.[id]?.testCode || id;
}

/** #2252 -- warning affordance for Class.testDisagreement (#2251): a class
 * whose own bound test disagrees with what its rides actually resolve to.
 * `flagged` renders a `warning`-color Chip (never brand `primary` -- dark
 * grey, disappears; same FvdBindingChip convention) naming the class-level
 * test and every distinct real test its rides resolve to (card #2252's
 * class-814 example: a class's rides can span more than two tests, so this
 * is a joined list, not a fixed two-slot pair), plus WHICH signal(s) fired
 * (score-common/constants.js TEST_DISAGREEMENT_SIGNALS doc comment).
 *
 * Deliberately offers no "fix it here" control: ride.testId is re-derived
 * from the FVD daysheet on every reconcile (apps/score-api/src/handlers/
 * daysheet.js:675), so anything written at this end would be overwritten by
 * the next sync. The correction belongs in FVD -- this is "tell a human",
 * full stop (per Jaybill 2026-09-03 on card #2252). */
export default function TestDisagreementChip({ testDisagreement, classTestId, rideTestIds, testsById }) {
  const { t } = useTranslation("scoreadmin");
  if (!testDisagreement?.flagged) return null;

  const classLabel = testLabel(classTestId, testsById);
  const rideLabels = (rideTestIds ?? []).map((id) => testLabel(id, testsById));
  const rideText = rideLabels.length ? rideLabels.join(", ") : t("ring.testDisagreement.loadingRides");
  const signals = testDisagreement.signals?.length ? testDisagreement.signals : [];

  return (
    <Tooltip
      title={
        <Stack spacing={0.5} sx={{ py: 0.5 }}>
          {signals.map((signal) => {
            const detailKey = SIGNAL_DETAIL_KEYS[signal];
            return (
              <Typography key={signal} variant="body2">
                {detailKey
                  ? t(detailKey, { classTest: classLabel, rideTests: rideText })
                  : t("ring.testDisagreement.unknownSignal", { signal, classTest: classLabel, rideTests: rideText })}
              </Typography>
            );
          })}
        </Stack>
      }
    >
      <Chip
        size="small"
        color="warning"
        icon={<WarningAmberIcon fontSize="small" />}
        label={t("ring.testDisagreement.badge")}
        data-testid="test-disagreement-chip"
        sx={{ ml: 1, cursor: "default" }}
      />
    </Tooltip>
  );
}
